import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Multi-select status filter (PLAN §3.3) — a compact MUI `Autocomplete`
 * rendering selected values as chips. Server applies WHERE-IN on the
 * enum column; no client-side filtering of a partial page (§1.5).
 *
 * Options carry an i18n `labelKey` rather than literal text so every
 * status label is translated (CLAUDE.md absolute rule #11).
 */
export interface StatusOption {
  value: string;
  /** i18n key resolved to the display label. */
  labelKey: string;
}

export interface StatusMultiSelectProps {
  /** Currently selected status values. */
  status: string[];
  onChange: (next: string[]) => void;
  options: StatusOption[];
  /** Field label (i18n key resolved by the caller, or defaults). */
  label?: string;
}

export const StatusMultiSelect = ({
  status,
  onChange,
  options,
  label,
}: StatusMultiSelectProps): ReactElement => {
  const { t } = useTranslation();
  const selected = options.filter((o) => status.includes(o.value));

  return (
    <Autocomplete<StatusOption, true>
      multiple
      size="small"
      options={options}
      value={selected}
      onChange={(_event, next) => onChange(next.map((o) => o.value))}
      getOptionLabel={(o) => t(o.labelKey)}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      renderValue={(value, getItemProps) =>
        value.map((option, index) => {
          const { key, ...itemProps } = getItemProps({ index });
          return <Chip key={key} size="small" label={t(option.labelKey)} {...itemProps} />;
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? t('filters.statusLabel')}
          placeholder={selected.length === 0 ? t('filters.statusPlaceholder') : undefined}
        />
      )}
      sx={{ minWidth: 220 }}
    />
  );
};
